/* eslint-disable react/prop-types */
import { useState, useEffect, useMemo } from 'react';
import { Button, Grid, Typography, Tooltip } from '@mui/material';
import './local-filters.scss';
import { useFormik } from 'formik';
import { FormField2, IDynamicFormValue } from 'src/lib';
import { DynamicFormFieldRenderer } from 'src/components/analytics';
import * as Yup from 'yup';
import { withStyles } from '@mui/styles';

interface ILocalFilterProps {
  onApply: (value: IDynamicFormValue) => void;
  onChange: (value: IDynamicFormValue) => void;
  isLocalFiltersReset: boolean;
  formFields: FormField2[];
  initialValues: IDynamicFormValue;
}

const ErrorTooltip = withStyles({
  tooltip: {
    color: "white",
    backgroundColor: "red"
  }
})(Tooltip);

export default function LocalFilters({
  onApply,
  onChange,
  formFields,
  initialValues,
  isLocalFiltersReset
}: ILocalFilterProps) {
  const [validationSchema, setValidationSchema] = useState(
    Yup.object().shape({})
  );
  const [isMinMaxValid, setIsMinMaxValid] = useState(true);
  const [isTopnValid, setIsTopnValid] = useState(true);
  const [isIncrementValid, setIsIncrementValid]=useState(true);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const errorText={
    empty: 'some field are empty',
    invalidMinMax: 'invalid min max value',
    invalidForm: 'Some fields are invalid'
  };

  const localFilterForm = useFormik<IDynamicFormValue>({
    initialValues,
    validateOnChange: true,
    validationSchema,
    onSubmit: onApply,
  });

  const handleChange = async (value: unknown, field: FormField2) => {
    const previousValue = localFilterForm.values;

    const newValue = {
      ...previousValue,
      [field.name]: value
    };

    //validation for min_price field
     if(field.name === 'min_price'){
          if (value ===''){
            setIsMinMaxValid(false);
            setIsTooltipVisible(true);
            setErrorMessage(errorText.empty);
          }
          else if(value > newValue.max_price ){
            setIsMinMaxValid(false);
            setIsTooltipVisible(true);
            setErrorMessage(errorText.invalidMinMax);
          }
          else{
              setIsMinMaxValid(true);
              if(!isIncrementValid || !isTopnValid){
                setIsTooltipVisible(true);
                setErrorMessage(errorText.invalidForm);
              }
              else{
                setIsTooltipVisible(false);
              }
          }
     }
 
     //validation for max_price field
      if(field.name === 'max_price'){
          if (value ===''){
            setIsMinMaxValid(false);
            setIsTooltipVisible(true);
            setErrorMessage(errorText.empty);
          }
          else if(value < newValue.min_price ){
            setIsMinMaxValid(false);
            setIsTooltipVisible(true);
            setErrorMessage(errorText.invalidMinMax);
          }
          else{
              setIsMinMaxValid(true);
              if(!isIncrementValid || !isTopnValid){
                setIsTooltipVisible(true);
                setErrorMessage(errorText.invalidForm);
              }
              else{
                setIsTooltipVisible(false);
              }
          }
     }

     //validation for Increment field
     if(field.name === 'increment'){
          if(!Number(value) && value!==0){
            setIsIncrementValid(false);
            setIsTooltipVisible(true);
            setErrorMessage(errorText.empty);
          }
          else if( Number(value) < 1){
            setIsIncrementValid(false);
            setIsTooltipVisible(true);
            setErrorMessage(errorText.invalidForm);
          }
          else{
              setIsIncrementValid(true);
              if(!isTopnValid || !isMinMaxValid){
              setErrorMessage(errorText.invalidForm);
              }
              else{
              setIsTooltipVisible(false);
              }
          }
      }

     //validation of topn field
     if(field.name === 'top_n'){
        if(value ===''){
            setIsTopnValid(false);
            setIsTooltipVisible(true);
            setErrorMessage(errorText.empty);
        }
        else if(value === 0 || Number(value) < 0){
            setIsTopnValid(false);
            setIsTooltipVisible(true);
            setErrorMessage(errorText.invalidForm);
        }
        else{
            setIsTopnValid(true);
            if(!isMinMaxValid || !isIncrementValid){
              setIsTooltipVisible(true);
              setErrorMessage(errorText.invalidForm);
            }
            else{
              setIsTooltipVisible(false);
            }
        }
     }


    localFilterForm.setValues(newValue);
    onChange(newValue);
  };

  useEffect(() => {
    if (isLocalFiltersReset) {
      const resetValue = formFields.reduce<IDynamicFormValue>(
        (prev, curr) => ({ ...prev, [curr.name]: curr.default_value }),
        {}
      );
      localFilterForm.resetForm(resetValue);
      onChange(resetValue);
    }
  }, [isLocalFiltersReset]);


  return formFields.length ? (
    <Grid className='local-filter-container' container alignItems='flex-end'>
      <Grid item container spacing={1} xs='auto' justifyContent='stretch'>
        {formFields.map((control) => (
          <Grid
            item
            className={`${
              control.control_type === 'NUMBER'
                ? 'number-control'
                : 'select-control'
            }`}
            key={control.name}
          >
            <Grid
              container
              alignItems='flex-end'
              justifyContent='space-between'
            >
              <Grid sx={{ width: '100%' }}>
                <Typography
                  component='label'
                  fontSize='10px'
                  sx={{
                    visibility: control.alias ? 'visible' : 'hidden'
                  }}
                >
                  {control.alias || 'Top N'}
                </Typography>
                <DynamicFormFieldRenderer
                  form={localFilterForm}
                  handleChange={(value) => handleChange(value, control)}
                  field={control}
                />
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>
      <Grid
        pl={1}
        pt={2}
        item
        xs='auto'
        container
        spacing={1}
        justifyContent='center'
      >
        <Grid item xs>
          {/* {localFilterForm.isValid}
          {isMinMaxValid} */}
          <ErrorTooltip title={errorMessage} open={isTooltipVisible}>
            <Button
              disabled={!localFilterForm.isValid || !isMinMaxValid || !isIncrementValid || !isTopnValid}
              type='submit'
              variant='outlined'
              onClick={() => onApply(localFilterForm.values)}
            >
              Apply
            </Button>
          </ErrorTooltip>
        </Grid>
      </Grid>
    </Grid>
  ) : null;
}
